import React from 'react';
import Dialog from '@mui/material/Dialog';
import { useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import { useReactiveVar } from '@apollo/client';
import { ButtonProps } from '@mui/material/Button';
import * as Device from 'react-device-detect';

import { loginStatusVar } from '@context';
import Login from './login';

export interface LoginDialogProps {
  button?: React.ReactElement<ButtonProps>;
  redirect?: string | null;
}

export function AuthenticationDialog(props: Readonly<LoginDialogProps>) {
  const { button, redirect = null } = props;
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const loginStatus = useReactiveVar(loginStatusVar);
  const { isLoggedIn, isSessionExpired, shouldLogFirst } = loginStatus;
  const { isLandscape } = Device.useMobileOrientation();

  const isFullScreenDisplay = Device.isDesktop && !isSessionExpired;
  const isLandscapeMobile = Device.isMobileOnly && isLandscape;

  React.useEffect(() => {
    setOpen(!isLoggedIn && (isSessionExpired || shouldLogFirst));
  }, [isLoggedIn, isSessionExpired, shouldLogFirst]);

  const handleClickOpen = async () => {
    await requestPermissions();
    setOpen(true);
  };

  const handleClickClose = (event: React.MouseEvent, reason: string) => {
    loginStatusVar({
      ...loginStatus,
      isSessionExpired: false,
      shouldLogFirst: false,
      redirection: null,
    });

    if (!isSessionExpired && !shouldLogFirst && !isLoggedIn) {
      setOpen(false);
    }
  };

  return (
    <>
      {button &&
        React.cloneElement(button, {
          disabled: loginStatus.isLoggedIn,
          loading: loginStatus.isLogging,
          onClick: () => {
            handleClickOpen();
          },
        })}
      <Dialog
        scroll="body"
        fullScreen={isLandscapeMobile}
        fullWidth
        open={open}
        maxWidth="xs"
        onClose={handleClickClose}
        slotProps={{
          paper: {
            sx: {
              minHeight: 'auto',
              position: 'relative',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              backgroundColor: theme.palette.background.paper,
              p: 5,
            },
          },
        }}
      >
        <IconButton onClick={handleClickClose} sx={{ position: 'absolute', top: 10, right: 10 }}>
          <theme.icons.close sx={{ color: theme.palette.text.secondary }} />
        </IconButton>
        <Login redirect={redirect} />
      </Dialog>
    </>
  );
}

async function requestPermissions() {
  if (typeof window !== 'undefined' && typeof Notification !== 'undefined') {
    let permission = 'granted';

    if (Notification.permission === 'default') {
      permission = await Notification.requestPermission();
    }

    return permission === 'granted';
  } else {
    import.meta.env.DEV && console.warn('Notifications not supported in this browser.');
  }
}
